// Layer 4 (Data Schema, module-local): Module 10 — the daily bottleneck
// meeting, shift handover and monthly CCC review over one seeded register.
// [syn] screenshot-grade seed — paul-tarp.A20 (#422), the `a20-meeting`
// stream. Field names follow the v19 M10 §4 reference-sheet columns
// (ThreeWEntry, CCCCase, MeetingInstance) so the register reads like the
// live sheet a team already keeps, not a renamed abstraction.
//
// Cross-stream ids (brief-a20-common.md): 3W-101…124, CCC-07…12; WO- ids
// cite the maintenance stream's own register read-only. CV-01…09 mirror
// Risk's `CONTROL_VERIFICATION_CHECKS` (risk/seed.ts owns the rows; the
// meeting reads them — polish.P20, comp adjudication 5).
//
// Named people are the org's established roster (`bunker-hill-org-seed.ts`)
// plus the handler names already used across `bunker-hill-advisory-seed.ts`
// and the maintenance work-order table (`bunker-hill-seed.ts`): S. Park
// (metallurgist), L. Chen (senior metallurgist), D. Okafor (CRO), M. Reid
// (operator), A. Chen (superintendent, day), D. Lee (ops supervisor,
// night), K. Jones / J. Smith / T. Brown (maintenance). No name here is
// invented — the seat-sre (senior reliability engineer) role has no
// assigned person, so CCC cases routed there cite the seat label alone
// (fixer round, MF13).

import { BM01_FEED } from '@/versions/v1/shared/bunker-hill-seed';
import { formatValue } from '@/versions/v1/shared/reading.derive';
import { CONTROL_VERIFICATION_CHECKS as RISK_CONTROL_VERIFICATION_CHECKS } from '@/versions/v1/modules/risk/seed';

export type ProcessArea =
  | 'Crushing'
  | 'Grinding'
  | 'Pb flotation'
  | 'Zn flotation'
  | 'Thickening & paste'
  | 'Reagents & services';

export const PROCESS_AREAS: readonly ProcessArea[] = [
  'Crushing',
  'Grinding',
  'Pb flotation',
  'Zn flotation',
  'Thickening & paste',
  'Reagents & services',
];

/** Step 3 opens with the plant's constraint (v19 §2, §12) — bottleneck
 * first, then the rest in the order a room would walk the circuit. */
export const AREA_ORDER_BOTTLENECK_FIRST: readonly ProcessArea[] = [
  'Grinding',
  'Thickening & paste',
  'Crushing',
  'Pb flotation',
  'Zn flotation',
  'Reagents & services',
];

/** [syn] open + closed 3W rows raised per area over the trailing 30 days —
 * distinct from today's 24 open rows, which the register itself holds.
 * Grinding dominant because it is the constraint (fixer round, MF5). */
export const PARETO_30D_COUNTS: Readonly<Record<ProcessArea, number>> = {
  Grinding: 11,
  'Zn flotation': 7,
  'Thickening & paste': 5,
  Crushing: 4,
  'Pb flotation': 3,
  'Reagents & services': 2,
};

export type ThreeWSource =
  | 'meeting'
  | 'field capture'
  | 'control check'
  | 'condition monitoring'
  | 'KPI tile';

export type ThreeWEntry = {
  id: string;
  area: ProcessArea;
  issue: string;
  what: string;
  /** The fallback task if the preventive `what` fails — one imperative clause,
   * written only on the rows the register ranks critical or high (CCC /
   * escalated / control-check / condition-monitoring source). Rendered as the
   * What cell's second line, behind `to reduce impact` (S2a). */
  reduceImpact?: string;
  who: string;
  orderNo?: string;
  safety: boolean;
  production: boolean;
  resources: boolean;
  originalCommit: string;
  /** `null` reads as "—" — escalated today, no fix date committed yet. */
  currentCommit: string | null;
  doneAwaitingAck?: boolean;
  escalated: boolean;
  cccCaseId?: string;
  source: ThreeWSource;
};

const MEETING_DATE = '2026-06-08';
const PREVIOUS_MEETING_DATE = '2026-06-07';

export const MEETING_CURSOR = MEETING_DATE;
export const PREVIOUS_MEETING_CURSOR = PREVIOUS_MEETING_DATE;

export const THREE_W_ENTRIES: readonly ThreeWEntry[] = [
  // Crushing
  {
    id: '3W-101',
    area: 'Crushing',
    issue: 'ROM stockpile moisture trending high after rain',
    what: 'Blend drier ore from the east pad; monitor crusher amp draw',
    who: 'M. Reid',
    safety: false,
    production: true,
    resources: false,
    originalCommit: '2026-06-05',
    currentCommit: '2026-06-05',
    escalated: false,
    source: 'field capture',
  },
  {
    id: '3W-102',
    area: 'Crushing',
    issue: 'Primary crusher liner wear approaching spec',
    what: 'Liner set replaced during the 07 Jun maintenance window',
    who: 'K. Jones',
    orderNo: 'PO-2278',
    safety: false,
    production: true,
    resources: true,
    originalCommit: '2026-06-03',
    currentCommit: '2026-06-07',
    doneAwaitingAck: true,
    escalated: false,
    source: 'meeting',
  },
  {
    id: '3W-103',
    area: 'Crushing',
    issue: 'Storm forecast overnight — ROM pad drainage risk',
    what: 'Clear pad drains before shift end; watch stockpile moisture into tomorrow',
    who: 'D. Lee',
    safety: true,
    production: true,
    resources: false,
    originalCommit: MEETING_DATE,
    currentCommit: '2026-06-09',
    escalated: false,
    source: 'field capture',
  },
  {
    id: '3W-104',
    area: 'Crushing',
    issue: 'Apron feeder belt tracking drift',
    what: 'Aligning idlers in stages around production windows',
    who: 'J. Smith',
    safety: true,
    production: true,
    resources: false,
    originalCommit: '2026-05-18',
    currentCommit: '2026-06-15',
    escalated: false,
    source: 'meeting',
  },

  // Grinding
  {
    id: '3W-105',
    area: 'Grinding',
    // K10c item 4: one "design" and two lines ("BM01 fresh feed 71.7 stph vs
    // 75.0 stph design"), the same pair the KPI tile and the variation read.
    issue: `BM01 fresh feed ${formatValue(BM01_FEED, { withUnit: true })} vs ${formatValue(BM01_FEED, { value: BM01_FEED.design, withUnit: true })} design`,
    what: 'Tuning feeder speed loop; watch circulating load',
    who: 'D. Okafor',
    safety: false,
    production: true,
    resources: false,
    originalCommit: MEETING_DATE,
    currentCommit: '2026-06-09',
    escalated: false,
    source: 'KPI tile',
  },
  {
    id: '3W-106',
    area: 'Grinding',
    issue: 'Cyclone cluster feed-density sensor drifting',
    what: 'Sensor recalibrated against the manual density check',
    who: 'K. Jones',
    safety: false,
    production: true,
    resources: false,
    originalCommit: '2026-06-05',
    currentCommit: '2026-06-06',
    doneAwaitingAck: true,
    escalated: false,
    source: 'meeting',
  },
  {
    id: '3W-109',
    area: 'Grinding',
    issue: 'Regrind mill drive tripping on overload',
    what: 'Adjust breaker settings and inspect drive alignment',
    reduceImpact: 'Take the regrind mill out of circuit and complete the drive inspection before restart',
    who: 'T. Brown',
    // No order no. — v19 §5's reference row for this issue carries none
    // (fixer round, MF7: WO-435 is the regrind mill's liner-inspection PM,
    // a different job — see HANDOVER_TASKS below).
    safety: true,
    production: true,
    resources: false,
    originalCommit: MEETING_DATE,
    currentCommit: null,
    escalated: true,
    cccCaseId: 'CCC-11',
    source: 'meeting',
  },
  {
    id: '3W-121',
    area: 'Grinding',
    issue: 'BM01 trunnion bearing temperature monitoring after the WO-441 restart',
    what: 'Oil sample pulled this shift; inspect/replace tracked on WO-441',
    reduceImpact: 'Trend the trunnion bearing temperature hourly and bring the inspection forward',
    who: 'J. Smith',
    orderNo: 'WO-441',
    safety: true,
    production: true,
    resources: false,
    originalCommit: MEETING_DATE,
    currentCommit: '2026-06-11',
    escalated: false,
    source: 'condition monitoring',
  },

  // Pb flotation
  {
    id: '3W-107',
    area: 'Pb flotation',
    issue: 'Pb rougher pH dragging below setpoint',
    what: 'Lime addition rate increased; recheck next round',
    who: 'A. Chen',
    safety: false,
    production: true,
    resources: false,
    originalCommit: MEETING_DATE,
    currentCommit: '2026-06-10',
    escalated: false,
    source: 'KPI tile',
  },
  {
    id: '3W-108',
    area: 'Pb flotation',
    issue: 'Pb thickener flocculant dosing pump wear',
    what: 'Awaiting spare diaphragm; monitor dose rate manually',
    who: 'K. Jones',
    safety: false,
    production: true,
    resources: true,
    originalCommit: '2026-06-06',
    currentCommit: '2026-06-06',
    escalated: false,
    source: 'meeting',
  },
  {
    id: '3W-110',
    area: 'Pb flotation',
    issue: 'Pb concentrate filter cloth blinding',
    what: 'Cloth change tracked on WO-436; filtration rate holding for now',
    who: 'K. Jones',
    orderNo: 'WO-436',
    safety: false,
    production: true,
    resources: true,
    originalCommit: MEETING_DATE,
    currentCommit: '2026-06-09',
    escalated: false,
    source: 'field capture',
  },
  {
    id: '3W-111',
    area: 'Pb flotation',
    issue: 'Pb rougher feed valve opening and closing by itself',
    what: 'Monitor; valve actuator replacement on order',
    reduceImpact: 'Hold the Pb rougher feed valve on manual and control the circuit from the control room',
    who: 'A. Chen',
    safety: true,
    production: true,
    resources: false,
    originalCommit: '2026-05-09',
    currentCommit: '2026-06-17',
    escalated: true,
    cccCaseId: 'CCC-07',
    source: 'control check',
  },

  // Zn flotation
  {
    id: '3W-112',
    area: 'Zn flotation',
    issue: 'Zn float pH low — 10.2 vs SP 10.8–11.2',
    what: 'Lime addition adjusted; retest this round',
    who: 'D. Okafor',
    safety: false,
    production: true,
    resources: false,
    originalCommit: MEETING_DATE,
    currentCommit: '2026-06-09',
    escalated: false,
    source: 'KPI tile',
  },
  {
    id: '3W-113',
    area: 'Zn flotation',
    issue: 'Zn rougher air flow low',
    what: 'Blower inlet filter checked; scheduling a clean',
    who: 'S. Park',
    safety: false,
    production: true,
    resources: false,
    originalCommit: MEETING_DATE,
    currentCommit: '2026-06-10',
    escalated: false,
    source: 'field capture',
  },
  {
    id: '3W-114',
    area: 'Zn flotation',
    issue: 'Zn cleaner froth stability marginal',
    what: 'Reagent split adjusted; froth confirmed stable next round',
    who: 'S. Park',
    safety: false,
    production: true,
    resources: false,
    originalCommit: '2026-06-06',
    currentCommit: '2026-06-07',
    doneAwaitingAck: true,
    escalated: false,
    source: 'KPI tile',
  },
  {
    id: '3W-115',
    area: 'Zn flotation',
    issue: 'Zn cleaner froth camera offline',
    what: 'Replacement unit requested',
    who: 'M. Reid',
    safety: false,
    production: false,
    resources: true,
    originalCommit: MEETING_DATE,
    currentCommit: '2026-06-12',
    escalated: false,
    source: 'field capture',
  },

  // Thickening & paste
  {
    id: '3W-116',
    area: 'Thickening & paste',
    issue: 'Paste backfill tonnes below target — 318 t vs 350 t/d',
    what: 'Binder rate held; underground demand confirmed for tomorrow',
    who: 'A. Chen',
    safety: false,
    production: true,
    resources: false,
    originalCommit: MEETING_DATE,
    currentCommit: '2026-06-09',
    escalated: false,
    source: 'KPI tile',
  },
  {
    id: '3W-117',
    area: 'Thickening & paste',
    issue: 'Zn thickener rake gearbox oil service due',
    what: 'Service tracked on WO-431',
    who: 'J. Smith',
    orderNo: 'WO-431',
    safety: false,
    production: true,
    resources: true,
    originalCommit: '2026-06-04',
    currentCommit: '2026-06-04',
    escalated: false,
    source: 'meeting',
  },
  {
    id: '3W-118',
    area: 'Thickening & paste',
    issue: 'TK01 rake torque control-verification check degraded',
    what: 'Field spot check flagged drive load trending toward the HH limit; inspection this shift',
    reduceImpact: 'Raise the TK01 rakes and cut the thickener feed if the torque check degrades again',
    who: 'D. Lee',
    safety: true,
    production: true,
    resources: false,
    originalCommit: '2026-06-07',
    currentCommit: '2026-06-09',
    escalated: false,
    source: 'control check',
  },
  {
    id: '3W-119',
    area: 'Thickening & paste',
    issue: 'Paste binder delivery delayed',
    what: 'Supplier re-committed; ration binder rate until delivery lands',
    who: 'A. Chen',
    safety: false,
    production: true,
    resources: true,
    originalCommit: '2026-06-01',
    currentCommit: '2026-06-10',
    escalated: false,
    source: 'meeting',
  },

  // Reagents & services
  {
    id: '3W-120',
    area: 'Reagents & services',
    issue: 'Aero 5100 collector stock below 7 days cover',
    what: 'Delivery confirmed and received this morning',
    who: 'S. Park',
    orderNo: 'PO-2287',
    safety: false,
    production: false,
    resources: true,
    originalCommit: '2026-06-02',
    currentCommit: MEETING_DATE,
    doneAwaitingAck: true,
    escalated: false,
    source: 'KPI tile',
  },
  {
    id: '3W-122',
    area: 'Reagents & services',
    issue: 'ZnSO₄ / SMBS solution registration in LIMS delayed',
    what: 'Register final tails and solution batches before next round',
    who: 'M. Reid',
    safety: false,
    production: true,
    resources: false,
    originalCommit: MEETING_DATE,
    currentCommit: '2026-06-09',
    escalated: false,
    source: 'meeting',
  },
  {
    id: '3W-123',
    area: 'Reagents & services',
    issue: 'CuSO₄ activation rate running above design — 155 g/t vs 150',
    what: 'Adequate for now; trending over the coming week',
    who: 'S. Park',
    safety: false,
    production: true,
    resources: false,
    originalCommit: MEETING_DATE,
    currentCommit: '2026-06-15',
    escalated: false,
    source: 'meeting',
  },
  {
    id: '3W-124',
    area: 'Reagents & services',
    issue: 'Process water booster pump vibration trending',
    what: 'Added to the vibration route; watch trend over the week',
    reduceImpact: 'Start the standby booster pump and take the vibrating pump out for balancing',
    who: 'K. Jones',
    safety: false,
    production: true,
    resources: false,
    originalCommit: MEETING_DATE,
    currentCommit: '2026-06-14',
    escalated: false,
    source: 'condition monitoring',
  },
];

// --- Meeting instances (30-day KPI window) ----------------------------

export type MeetingInstance = {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  attendees: readonly string[];
  onTimeStart: boolean;
  finishedWithinWindow: boolean;
};

const MEETING_ATTENDEES = ['S. Park', 'A. Chen', 'J. Smith', 'D. Okafor'];

/** [syn] one late start (18 May) and one over-window finish (28 May) in the
 * trailing 21 meetings — the rest hit the 08:30 start and the 20-minute
 * window. Yields the on-time-start and within-window rates the header
 * strip reports. */
const PRIOR_MEETING_DATES: readonly string[] = [
  '2026-05-18', '2026-05-19', '2026-05-20', '2026-05-21', '2026-05-22',
  '2026-05-23', '2026-05-24', '2026-05-25', '2026-05-26', '2026-05-27',
  '2026-05-28', '2026-05-29', '2026-05-30', '2026-05-31', '2026-06-01',
  '2026-06-02', '2026-06-03', '2026-06-04', '2026-06-05', '2026-06-06',
  '2026-06-07',
];
const LATE_START_DATES = new Set(['2026-05-18']);
const OVER_WINDOW_DATES = new Set(['2026-05-28']);

export const PRIOR_MEETING_INSTANCES: readonly MeetingInstance[] = PRIOR_MEETING_DATES.map(
  (date) => ({
    id: `meeting-${date}`,
    date,
    startTime: LATE_START_DATES.has(date) ? '08:37' : '08:30',
    endTime: OVER_WINDOW_DATES.has(date) ? '08:58' : '08:47',
    attendees: MEETING_ATTENDEES,
    onTimeStart: !LATE_START_DATES.has(date),
    finishedWithinWindow: !OVER_WINDOW_DATES.has(date),
  }),
);

export const TODAY_MEETING_INSTANCE: MeetingInstance = {
  id: `meeting-${MEETING_DATE}`,
  date: MEETING_DATE,
  startTime: '08:30',
  endTime: '08:47',
  attendees: MEETING_ATTENDEES,
  onTimeStart: true,
  finishedWithinWindow: true,
};

export const ALL_MEETING_INSTANCES: readonly MeetingInstance[] = [
  ...PRIOR_MEETING_INSTANCES,
  TODAY_MEETING_INSTANCE,
];

// --- Control-verification checks (this shift) --------------------------

export type ControlVerificationCheck = {
  id: string;
  control: string;
  interlockRef: string;
  result: 'pass' | 'degraded' | 'fail';
  onTime: boolean;
  resultingThreeWId?: string;
};

/** The shift's CV checks, read from Risk's one register (`risk/seed.ts`
 * `CONTROL_VERIFICATION_CHECKS`, CV-01…09 over IL-01…09) in the shape the
 * handover table and header tally read — id, control label, interlock,
 * result, on-time and the resulting 3W. Risk owns the rows (polish.P20, comp
 * adjudication 5); this is a re-shape, not a second copy. */
export const CONTROL_VERIFICATION_CHECKS: readonly ControlVerificationCheck[] = RISK_CONTROL_VERIFICATION_CHECKS.map(
  (check) => ({
    id: check.id,
    control: check.control,
    interlockRef: check.interlockId,
    result: check.lastResult,
    onTime: check.onTime,
    ...(check.resultingThreeWId ? { resultingThreeWId: check.resultingThreeWId } : {}),
  }),
);

// --- Shift handover -----------------------------------------------------

export type HandoverSafetyItem = {
  id: string;
  kind: 'Incident' | 'Process risk' | 'Crew note';
  detail: string;
  priority: 'P1' | 'P2' | 'Info';
};

export const HANDOVER_SAFETY_ITEMS: readonly HandoverSafetyItem[] = [
  {
    id: 'ho-1',
    kind: 'Incident',
    detail: 'Near-miss — mobile equipment / walkway, Bay 3',
    priority: 'P1',
  },
  {
    id: 'ho-2',
    kind: 'Process risk',
    detail: 'Storm forecast overnight — ROM pad drainage risk (3W-103)',
    priority: 'P2',
  },
  {
    id: 'ho-3',
    kind: 'Crew note',
    detail: 'Two operators returning from a 7-day rotation — briefed on last week’s 3W history',
    priority: 'Info',
  },
];

export type HandoverTask = {
  id: string;
  detail: string;
  assigned: string;
  kind: 'work order' | 'condition monitoring';
};

export const HANDOVER_TASKS: readonly HandoverTask[] = [
  { id: 'WO-441', detail: 'BM01 trunnion bearing — inspect / replace', assigned: 'J. Smith', kind: 'work order' },
  { id: 'WO-435', detail: 'Regrind mill liner inspection', assigned: 'T. Brown', kind: 'work order' },
  { id: 'WO-431', detail: 'Zn thickener rake gearbox oil service', assigned: 'J. Smith', kind: 'work order' },
  { id: 'CM-BM01-OIL', detail: 'BM01 trunnion bearing oil sample', assigned: 'J. Smith', kind: 'condition monitoring' },
];

// --- CCC register ---------------------------------------------------------

export type FailureCategory = 'Electrical' | 'Mechanical' | 'Metallurgical';
export type CccStage = 'Concern' | 'Cause & effect' | 'Containment' | 'Countermeasure';
export type CccStatus = 'Open' | 'Overdue' | 'Recurred — reopened' | 'Closed — held';

export type CCCCase = {
  id: string;
  sourceThreeWId?: string;
  concern: string;
  failureCategory: FailureCategory;
  /** Reliability engineer or control metallurgist running the case
   * day-to-day (v19 M10 §6) — the org's established roster, never invented
   * (fixer round, MF13). */
  lead: string;
  /** Root-cause reviewer's seat. `seat-senior-met` is assigned (L. Chen);
   * `seat-sre` has no assigned person, so cases routed there cite the seat
   * label alone. */
  routedTo: 'Senior metallurgist' | 'Senior reliability engineer';
  stage: CccStage;
  committedReview: string | null;
  cycleCount: number;
  recurred: boolean;
  status: CccStatus;
};

export const CCC_CASES: readonly CCCCase[] = [
  {
    id: 'CCC-07',
    sourceThreeWId: '3W-111',
    concern: 'Pb rougher feed valve cycling open/closed unattended',
    failureCategory: 'Electrical',
    lead: 'T. Brown',
    routedTo: 'Senior reliability engineer',
    stage: 'Countermeasure',
    committedReview: '2026-06-02',
    cycleCount: 3,
    recurred: false,
    status: 'Overdue',
  },
  {
    id: 'CCC-08',
    concern: 'Belt scale WIT-021 drift',
    failureCategory: 'Mechanical',
    lead: 'K. Jones',
    routedTo: 'Senior reliability engineer',
    stage: 'Containment',
    committedReview: '2026-06-05',
    cycleCount: 1,
    recurred: false,
    status: 'Overdue',
  },
  {
    id: 'CCC-09',
    concern: 'Zn recovery grade excursion',
    failureCategory: 'Metallurgical',
    lead: 'S. Park',
    routedTo: 'Senior metallurgist',
    stage: 'Countermeasure',
    committedReview: '2026-05-20',
    cycleCount: 10,
    recurred: false,
    status: 'Closed — held',
  },
  {
    id: 'CCC-10',
    concern: 'Cyclone feed pump wear — recurring',
    failureCategory: 'Mechanical',
    lead: 'K. Jones',
    routedTo: 'Senior reliability engineer',
    stage: 'Countermeasure',
    // [syn] re-committed on reopening (fixer round, SF8) — was 2026-05-28,
    // already past due at the demo cursor and uncounted as overdue.
    committedReview: '2026-06-22',
    cycleCount: 4,
    recurred: true,
    status: 'Recurred — reopened',
  },
  {
    id: 'CCC-11',
    sourceThreeWId: '3W-109',
    concern: 'Regrind mill drive tripping on overload',
    failureCategory: 'Electrical',
    lead: 'T. Brown',
    routedTo: 'Senior reliability engineer',
    stage: 'Concern',
    committedReview: '2026-06-28',
    cycleCount: 0,
    recurred: false,
    status: 'Open',
  },
  {
    id: 'CCC-12',
    concern: 'Belt scale WIT-021 — secondary drift check',
    failureCategory: 'Mechanical',
    lead: 'K. Jones',
    routedTo: 'Senior reliability engineer',
    stage: 'Cause & effect',
    committedReview: '2026-06-20',
    cycleCount: 2,
    recurred: false,
    status: 'Open',
  },
];
